import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { AppModelModalComponent } from '@nimmsta/ng-commons-lib/forms';
import { AuthService } from '@nimmsta/ng-commons-lib/auth';
import {
  Column,
  DataTableComponent,
  EmptyTableComponent,
  IconAction,
  NimmstaDataSource,
} from '@nimmsta/ng-commons-lib/data-table';
import { DateTimePipe, StatusBadgePipe } from '@nimmsta/ng-commons-lib/pipes';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { Organization } from '@nimmsta/ng-commons-lib/models';
import { PartnerOrganizationService } from '@nimmsta/ng-commons-lib/services/partner-organization';
import { PartnerOrganizationFormComponent } from './partner-organization-form.component';

@Component({
  selector: 'app-partner-organizations',
  templateUrl: './partner-organizations.component.html',
  imports: [
    PartnerOrganizationFormComponent,
    TranslatePipe,
    HeaderBarComponent,
    DataTableComponent,
    EmptyTableComponent,
    ErrorComponent,
    AppModelModalComponent,
  ],
  providers: [StatusBadgePipe, DateTimePipe],
})
export class PartnerOrganizationsComponent implements OnInit {
  private organizationService = inject(PartnerOrganizationService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);
  private statusBadge = inject(StatusBadgePipe);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private dateTimePipe = inject(DateTimePipe);

  organizationSource!: NimmstaDataSource<Organization>;

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent!: DataTableComponent<Organization>;

  @ViewChild('EditModal', { static: true })
  private editModalComponent!: AppModelModalComponent<Organization>;

  @ViewChild(PartnerOrganizationFormComponent, { static: true })
  private orgFormComponent!: PartnerOrganizationFormComponent;

  get columns(): Column[] {
    return [
      new Column('name', 'organizations.name', { sortable: true }),
      new Column('description', 'organizations.description', {
        sortable: true,
      }),
      new Column('address', 'organizations.address', { sortable: true }),
      new Column('status', 'organizations.status', {
        pipe: this.statusBadge,
        sortable: true,
      }),
      new Column('createdAt', 'createdAt', {
        pipe: this.dateTimePipe,
        sortable: true,
      }),
    ];
  }

  iconActions: IconAction<Organization>[] = [];

  error?: HttpErrorResponse;

  ngOnInit(): void {
    this.organizationSource = new NimmstaDataSource<Organization>(
      this.organizationService,
    );
    const editAction = new IconAction('edit', 'edit');
    this.iconActions.push(editAction);
    editAction.subscribe((id) => this.edit(id));
  }

  new() {
    this.translate.get('organizations.new').subscribe((value) => {
      this.authService.getCurrentUser().subscribe((user) => {
        const newOrg = new Organization();
        newOrg.parentOrganization = user.currentOrganization;
        this.openModal(value, true, newOrg);
      });
    });
  }

  edit(id: string) {
    this.translate.get('organizations.edit').subscribe((value) => {
      this.openModal(value, true, this.getSelectedObject(id)!);
    });
  }

  delete(id: string) {
    this.organizationService.delete(this.getSelectedObject(id)!).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  show(id: string) {
    this.router.navigate([id], { relativeTo: this.route });
  }

  private getSelectedObject(id: string): Organization | undefined {
    return this.organizationSource.data?.content
      .filter((d) => id === d.id)
      .pop();
  }

  onSave(): void {
    this.orgFormComponent.onSave();
  }

  onModalClose(): void {
    this.editModalComponent.hide();
    this.tableComponent.loadDataPage();
  }

  onLoading(loading: boolean): void {
    this.editModalComponent.loading = loading;
  }

  private openModal(
    title: string,
    editable: boolean,
    organization: Organization,
  ): void {
    this.orgFormComponent.editable = editable;
    this.orgFormComponent.setValue(organization);
    this.editModalComponent.title = title;
    this.editModalComponent.editable = editable;
    this.editModalComponent.show();
  }
}
